import { useEffect, useMemo, useState } from 'react'
import PayMethodView from './components/PayMethodView'
import { PaymentMethod, PaymentMethodFee } from '@/api/dashboard'
import { dashboardClient } from '@/api'
import { formatMoney, uuid4 } from '@/util'
import { SelectRegion } from './components/SelectRegion'
import { COUNTRY_BY_REGIONS } from './components/data'
import { KeyValue } from '@/types'
import {
  Badge,
  PageHeader,
  Table,
  TableCell,
  TableRow,
  TableRowSkeleton,
  TableZeroState,
  TooltipAlertCircle,
} from '@/ui'
import { usePaymentMethods } from '@/api/usePaymentMethods'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { getCountries } from '@/translates'
import { ChevronDown, ChevronRight } from '@/icons'
import { HACK_METHODS } from '@/layouts/payment-settings/hack_methods'
import { useTranslation } from 'react-i18next'
import { ModalPresetConfirm, Toggle, cn, getMotionProps, useModal } from '@dashboard/ui'
import { useCurrentUser } from '@/api/useCurrentUser'
import { useGenerateCompanyPath } from '@/libs'
import { AnimatePresence, motion } from 'framer-motion'

const MAX_COUNTRIES = 18

const HIDDEN_METHODS = ['paypal', 'adyen_plaid', 'adyen_plaid_initial', 'venmo']

const ALWAYS_ENABLED = [
  'apple_checkout',
  'google_checkout',
  'paypal',
  'checkout',
  'PayPal : micro-pricing',
  'PayPal : regular',
]

const entryMotionProps = getMotionProps({
  initial: { paddingTop: 0, height: 0, opacity: 0 },
  animate: { paddingTop: 0, height: 'auto', opacity: 1 },
  exit: { paddingTop: 0, height: 0, opacity: 0 },
})

export default function PaymentSettingsTable() {
  const { companyId, gameId } = useGenerateCompanyPath()
  const { t } = useTranslation()
  const { isAdmin } = useCurrentUser()

  const [methods, setMethods] = useState<PaymentMethod[]>([])

  const [expandedIds, setExpandedIds] = useState<string[]>([])

  const [regionFilterValue, setRegionFilterValue] = useState<string | null>(null)
  const { data, error, isLoading, refetch } = usePaymentMethods(companyId, gameId)
  const { data: gameSettings } = useGameSettingsQuery(companyId, gameId)
  const countries = getCountries()

  useEffect(() => {
    if (data && !isLoading) {
      const filtered_data = data.filter(it => {
        if (it.dashboard_show === false || !it.logo_url) {
          return false
        }

        if (HIDDEN_METHODS.includes(it.name)) {
          return false
        }

        return true
      })

      const hack_methods: PaymentMethod[] = []
      for (const h of HACK_METHODS) {
        const serverMethod = data.find(it => it.id == h.id)
        if (serverMethod) {
          h.settings.global_enabled = serverMethod.settings.global_enabled
          h.settings.enabled = serverMethod.settings.enabled
        }
        hack_methods.push(h)
      }

      setMethods(
        [...filtered_data, ...hack_methods].sort((a, b) =>
          a.settings.enabled == b.settings.enabled ? 0 : a.settings.enabled ? -1 : 1,
        ),
      )
    }
  }, [isLoading, data])

  const openStateModal = useModal<{ item: PaymentMethod }>(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: props.item.settings.enabled
          ? t('game-settings.ps-table.confirm-disable')
          : t('game-settings.ps-table.confirm-enable'),
        subtitle: t(
          props.item.settings.enabled
            ? 'game-settings.ps-table.confirm-disable.sub-message'
            : 'game-settings.ps-table.confirm-enable.sub-message',
          {
            name: props.item.caption,
          },
        ),
      }}
      cancel={{
        label: props.item.settings.enabled
          ? t('game-settings.ps-table.confirm.no-disable')
          : t('game-settings.ps-table.confirm.no-enable'),
      }}
      submit={{
        callback: async () => {
          await onUpdateConfirm(props.item)
          props.onClose()
        },
        color: 'danger',
        label: props.item.settings.enabled
          ? t('game-settings.ps-table.confirm.yes-turn-off')
          : t('game-settings.ps-table.confirm.yes-turn-on'),
      }}
    />
  ))

  const onUpdateConfirm = async (item: PaymentMethod) => {
    try {
      await dashboardClient.v1.updatePaymentMethods(item.id, companyId, gameId, {
        enabled: !item.settings.enabled,
      })
      refetch()
    } catch (e) {
      console.log(e)
    }
  }

  const isVerified = () => {
    return !!gameSettings?.onboarding_progress?.account_verified
  }

  const getFeePrc = (fee: PaymentMethodFee | null | undefined): React.ReactNode => {
    if (!fee || fee.fee_fix < 0) {
      return <></>
    }

    let str = '+' + formatMoney(fee.fee_fix, fee.currency || 'USD')

    if (typeof fee.fee_fix == 'string') {
      str = fee.fee_fix
    }

    return <Badge variant="gray-secondary">{str}</Badge>
  }

  const getFeeFix = (fee: PaymentMethodFee | null | undefined): React.ReactNode => {
    if (!fee) {
      return <></>
    }

    if (typeof fee.fee_prc == 'string') {
      return fee.fee_prc
    }

    return fee.fee_prc + '%'
  }

  const getCountry = (m: PaymentMethod, showAll = false) => {
    if (!m.supported_countries?.length) {
      return t('game-settings.ps-table.global')
    }

    let res = []

    for (let i = 0; i < m.supported_countries.length && (showAll || i < MAX_COUNTRIES); i++) {
      res.push(countries[m.supported_countries[i]])
    }

    return <div className="w-full whitespace-normal">{res.join(', ')}</div>
  }

  const hasMoreInfo = (m: PaymentMethod) => {
    return m.fee != undefined && m.fee.length > 1
  }

  const getState = (m: PaymentMethod) => {
    if (ALWAYS_ENABLED.includes(m.name) || ALWAYS_ENABLED.includes(m.id)) {
      return
    }

    if (!isVerified()) {
      return
    }

    if (m.settings.global_enabled === false) {
      return <div className="text-caption-sm uppercase text-text-disabled"> {t('game-settings.ps-table.request')}</div>
    }

    return (
      <div className="flex justify-end">
        <Toggle
          size="sm"
          disabled={!isAdmin}
          checked={m.settings.enabled}
          onChange={() => openStateModal({ item: m })}
        />
      </div>
    )
  }

  const renderFilterButtons = () => {
    return <SelectRegion value={regionFilterValue} onChange={value => setRegionFilterValue(value as string)} />
  }

  const renderFeeCell = (fee: PaymentMethodFee | null, className?: string) => {
    return (
      <div className={cn('flex w-full items-center justify-center', className)}>
        <div className="flex w-[150px] items-center justify-center gap-3">
          <div className="w-1/2 text-right">{getFeeFix(fee)}</div>
          <div className="w-1/2 text-left">{getFeePrc(fee)}</div>
        </div>
      </div>
    )
  }

  const renderRow = (m: PaymentMethod, idx: number) => {
    if (!hasMoreInfo(m)) {
      return (
        <TableRow key={m.id + idx} disableFocus>
          <TableCell className="py-5">
            <PayMethodView paymentMethod={m} />
          </TableCell>

          <TableCell className="py-5">{getCountry(m)}</TableCell>

          <TableCell className="py-5">
            {m.settings.global_enabled !== false && m.fee && m.fee.length > 0 && isVerified() ? (
              renderFeeCell(m.fee[0])
            ) : (
              <div className="py-3"> </div>
            )}
          </TableCell>

          <TableCell className="p-5 text-right">{isVerified() && getState(m)}</TableCell>
        </TableRow>
      )
    }

    const isExpanded = expandedIds.includes(m.id)
    const boxCountries: React.ReactNode[] = []
    const firstBox: string[] = []

    m.fee?.forEach(fee => {
      if (fee.country_code && countries[`${fee.country_code}`]) {
        boxCountries.push(countries[`${fee.country_code}`] as string)
      } else {
        boxCountries.push(t(`${fee.country_code}`))
      }

      let firstCol = (fee as unknown as KeyValue)['first_column'] as string
      if (firstCol) {
        firstBox.push(firstCol)
      }
    })

    return (
      <TableRow
        key={m.id}
        disableFocus
        className={cn(isExpanded && 'bg-fg-secondary', 'items-start transition-colors duration-1000')}
      >
        <TableCell className="py-5">
          <PayMethodView paymentMethod={m} />
          <AnimatePresence>
            {isExpanded && (
              <motion.div {...entryMotionProps} className="mt-3">
                {firstBox.map(it => (
                  <div className="ml-[42px] flex items-center py-3">{it}</div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </TableCell>

        <TableCell className="p-5 text-left">
          <div className="flex items-center">{getCountry(m)}</div>
          <AnimatePresence>
            {isExpanded && (
              <motion.div {...entryMotionProps} className="mt-3">
                {boxCountries.map(it => (
                  <div className="flex items-center py-3" key={uuid4()}>
                    {it}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </TableCell>
        <TableCell className="flex flex-col items-center p-5 text-center">
          {isVerified() && (
            <>
              <div
                className="ml-[42px] flex cursor-pointer select-none items-center gap-2 whitespace-nowrap"
                onClick={() => {
                  setExpandedIds(isExpanded ? expandedIds.filter(it => it != m.id) : [...expandedIds, m.id])
                }}
              >
                {isExpanded ? <ChevronDown style={{ width: '16px', height: '17px' }} /> : <ChevronRight />}
                {t(isExpanded ? 'game-settings.ps-table.less-info' : 'game-settings.ps-table.more-info')}
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div {...entryMotionProps} className="mt-3">
                    {m.fee?.map(it => renderFeeCell(it, 'py-3'))}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </TableCell>

        <TableCell className="p-5 text-right">{isVerified() && getState(m)}</TableCell>
      </TableRow>
    )
  }

  const filter = (list: PaymentMethod[]) => {
    if (!regionFilterValue) {
      return list
    }

    return list.filter(it => {
      if (regionFilterValue == 'Global') {
        return it.supported_countries.length == 0
      }
      let regionCountries = (COUNTRY_BY_REGIONS as KeyValue)[regionFilterValue] as Set<string>
      if (!regionCountries) {
        return true
      }
      return it.supported_countries.find(c => regionCountries.has(c))
    })
  }

  const readyMethods = useMemo(() => {
    let list = methods.filter(it => it.settings.global_enabled === true)

    let v = ['apple_checkout', 'google_checkout', 'paypal', 'checkout', 'PayPal']
    let viewMethods = list.filter(it => !v.includes(it.name))
    let firstMethods = list.filter(it => v.includes(it.name))

    return filter([...firstMethods, ...viewMethods.sort((a, b) => (a.caption || '').localeCompare(b.caption || ''))])
  }, [gameId, companyId, methods, regionFilterValue])

  const notReadyMethods = useMemo(() => {
    let list = methods.filter(it => it.settings.global_enabled === false)

    return filter(list.sort((a, b) => (a.caption || '').localeCompare(b.caption || '')))
  }, [regionFilterValue, methods])

  if (error) {
    return <TableZeroState className="h-[400px]" title={t('service-unavailable')} message={t('SomethingWentWrong')} />
  }

  return (
    <div>
      <PageHeader extra={renderFilterButtons()}>{t('sidebar.payment-methods')}</PageHeader>

      <Table data-testid="payment-methods-table">
        <TableRow variant="header">
          <TableCell>{t('game-settings.ps-table.name')}</TableCell>
          <TableCell className="px-2">{t('game-settings.ps-table.countries')}</TableCell>
          <TableCell className="flex items-center justify-center">
            {isVerified() && (
              <div className="w-[90px]">
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  {t('game-settings.ps-table.fee')}
                  <TooltipAlertCircle message={t('game-settings.ps-table.fee.tooltip')} />
                </span>
              </div>
            )}
          </TableCell>
          <TableCell className="text-right">{isVerified() && t('game-settings.ps-table.on-off')}</TableCell>
        </TableRow>

        {!isLoading && readyMethods.map((m, idx) => renderRow(m, idx))}
        {!isLoading && notReadyMethods.map((m, idx) => renderRow(m, idx))}
      </Table>

      {isLoading && <TableRowSkeleton rowCount={20} columnCount={4} />}
    </div>
  )
}
